import { useCallback, useEffect, useRef, useState } from "react";
import PageTitle from "../components/PageTitle";
import {
  fetchScreenshotsForAdmin,
  uploadScreenshot,
  updateScreenshot,
  deleteScreenshot,
  monthName,
} from "../api/opsApi";

// Putting a month's winner up, and editing or removing one that is already there.
//
// It is gated by the same portal session and administrator role the Interep admin pages use,
// on the server as well as here: the page only hides the form, routes/screenshots.js is what
// actually refuses anybody else.

const thisMonth = () => new Date().toISOString().slice(0, 7);

const emptyForm = () => ({ period: thisMonth(), title: "", description: "", credit: "" });

export default function ScreenshotsAdminPage() {
  const [entries, setEntries] = useState(null);
  const [denied, setDenied] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [editing, setEditing] = useState(null);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      setEntries(await fetchScreenshotsForAdmin());
      setDenied(false);
    } catch (error) {
      if (error.status === 401 || error.status === 403) setDenied(true);
      else setMessage({ bad: true, text: error.message });
      setEntries([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The chosen file is shown before it is sent, and the object URL is released when it changes.
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const field = (name) => ({
    value: form[name],
    onChange: (event) => setForm((current) => ({ ...current, [name]: event.target.value })),
  });

  const submit = async (event) => {
    event.preventDefault();
    setMessage(null);
    setBusy(true);
    try {
      if (editing) {
        await updateScreenshot(editing, form);
        setMessage({ text: "Saved." });
      } else {
        if (!file) throw new Error("Choose an image first.");
        await uploadScreenshot(file, form);
        setMessage({ text: "Winner published." });
      }
      setForm(emptyForm());
      setFile(null);
      setEditing(null);
      if (fileInput.current) fileInput.current.value = "";
      await load();
    } catch (error) {
      setMessage({ bad: true, text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (entry) => {
    setEditing(entry.id);
    setForm({
      period: entry.period,
      title: entry.title,
      description: entry.description ?? "",
      credit: entry.credit ?? "",
    });
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
    setMessage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (entry) => {
    // Deleting a winner takes the image with it, so it is confirmed first.
    if (!window.confirm(`Remove "${entry.title}" (${monthName(entry.period)})? The image is deleted.`)) return;
    setBusy(true);
    setMessage(null);
    try {
      await deleteScreenshot(entry.id);
      if (editing === entry.id) {
        setEditing(null);
        setForm(emptyForm());
      }
      await load();
      setMessage({ text: "Removed." });
    } catch (error) {
      setMessage({ bad: true, text: error.message });
    } finally {
      setBusy(false);
    }
  };

  if (denied) {
    return (
      <>
        <PageTitle>vWZZ - Screenshots admin</PageTitle>
        <div className="page-container shots-admin">
          <h1 className="page-title">Screenshots admin</h1>
          <p className="shots-empty">
            Sign in with an administrator account to manage the competition.{" "}
            <a href="/interep">Go to sign in</a>
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle>vWZZ - Screenshots admin</PageTitle>
      <div className="page-container shots-admin">
        <header className="shots-header">
          <h1 className="page-title">Screenshots admin</h1>
          <p className="page-subtitle">
            Publish the winner for a month, or edit one already up.
          </p>
        </header>

        <form className="shots-form" onSubmit={submit}>
          <h2>{editing ? "Edit entry" : "New winner"}</h2>

          <div className="shots-form-row">
            <label>
              <span>Month</span>
              <input type="month" required {...field("period")} />
            </label>
            <label>
              <span>Credited to</span>
              <input type="text" maxLength={80} placeholder="Pilot name or callsign" {...field("credit")} />
            </label>
          </div>

          <label>
            <span>Title</span>
            <input type="text" required maxLength={120} placeholder="Short title" {...field("title")} />
          </label>

          <label>
            <span>Description</span>
            <textarea rows={3} maxLength={600} placeholder="A line or two about the shot" {...field("description")} />
          </label>

          {editing ? (
            <p className="shots-note">The image stays as it is. Remove the entry and upload again to replace it.</p>
          ) : (
            <label>
              <span>Image (JPEG or PNG, up to 8 MB)</span>
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </label>
          )}

          {preview ? <img className="shots-preview" src={preview} alt="Chosen screenshot" /> : null}

          <div className="shots-form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Working..." : editing ? "Save changes" : "Publish winner"}
            </button>
            {editing ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() => {
                  setEditing(null);
                  setForm(emptyForm());
                  setMessage(null);
                }}
              >
                Cancel
              </button>
            ) : null}
            {message ? (
              <span className={`shots-message ${message.bad ? "is-bad" : ""}`} role="status">
                {message.text}
              </span>
            ) : null}
          </div>
        </form>

        <section className="shots-admin-list">
          <h2>Published</h2>
          {entries === null ? (
            <p className="shots-empty">Loading...</p>
          ) : !entries.length ? (
            <p className="shots-empty">Nothing published yet.</p>
          ) : (
            <ul>
              {entries.map((entry) => (
                <li key={entry.id}>
                  <img src={entry.image} alt={entry.title} loading="lazy" />
                  <div>
                    <span className="shots-month">{monthName(entry.period)}</span>
                    <strong>{entry.title}</strong>
                    {entry.credit ? <span>By {entry.credit}</span> : null}
                    {entry.description ? <p>{entry.description}</p> : null}
                  </div>
                  <div className="shots-admin-actions">
                    <button type="button" onClick={() => startEdit(entry)} disabled={busy}>
                      Edit
                    </button>
                    <button type="button" className="is-danger" onClick={() => remove(entry)} disabled={busy}>
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}
